<template>
  <v-container grid-list-lg fluid>
    <TitleBar title="Roles" />

    <v-layout row wrap>
      <v-flex xs12>
        <v-card outlined class="rounded-xl">
          <CardToolbar title="New Role" :isModal="false" />
          <v-card-text>
            <v-layout row wrap align-baseline>
              <v-flex xs12 sm2>
                <span class="font-weight-medium">Name</span>
              </v-flex>

              <v-flex xs12 sm8>
                <v-text-field
                  type="text"
                  outlined
                  hint="e.g. Sales Manager, Junior, Read Only"
                  v-model="role.name"
                  @focus="$event.target.select()"
                  :hide-details="nameErrors.length === 0"
                  :error-messages="nameErrors"
                  label="Name"
                  @input="$v.role.name.$touch()"
                />
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>
      </v-flex>

      <!-- Permissions -->
      <v-flex xs12>
        <v-card class="outlined rounded-xl">
          <v-card-text>
            <!-- Search box -->
            <v-text-field
              v-model="search"
              label="Search permissions"
              prepend-inner-icon="mdi-magnify"
              clearable
              outlined
              dense
              class="mb-4"
            />

            <!-- Permissions grouped by section -->
            <div v-for="(group, index) in filteredGroups" :key="index" class="mb-6">
              <!-- Group title -->
              <h3 class="subtitle-2 font-weight-bold mb-2">
                {{ group.name }}
              </h3>
              <v-divider class="mb-2" />

              <!-- Group permissions -->
              <v-row
                v-for="permission in group.permissions"
                :key="permission.id"
                align="center"
                class="py-2"
              >
                <!-- Name + Description -->
                <v-col cols="12" md="9" lg="10">
                  <span class="font-weight-medium">
                    {{ permission.name }}
                  </span>
                  <p class="font-weight-light mb-0">
                    {{ permission.description }}
                  </p>
                </v-col>

                <!-- Switch -->
                <v-col cols="12" md="3" lg="2">
                  <v-row align="center" no-gutters>
                    <span class="mr-2">OFF</span>

                    <v-switch
                      hide-details
                      class="ma-0"
                      v-model="permission.allow"
                      @change="switchChanged(permission.stringId)"
                      color="primary"
                    />

                    <span class="ml-2">ON</span>
                  </v-row>
                </v-col>

                <!-- Limited history label -->
                <!-- <v-col
                  cols="12"
                  md="9"
                  lg="10"
                  v-if="
                    permission.stringId === 'HISTORYLIMITEDACCESS' &&
                    limitedHistoryEnabled
                  "
                >
                  <span class="font-weight-medium">Number of days:</span>
                </v-col>

                <v-col
                  cols="12"
                  md="3"
                  lg="2"
                  v-if="
                    permission.stringId === 'HISTORYLIMITEDACCESS' &&
                    limitedHistoryEnabled
                  "
                >
                  <v-text-field
                    type="number"
                    outlined
                    dense
                    v-model="permission.metadata.days"
                    @focus="$event.target.select()"
                    label="Days"
                    :rules="[daysLimit]"
                  />
                </v-col> -->
              </v-row>
            </div>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <v-card-actions :style="{ 'background-color': $style.card.action }">
      <v-spacer></v-spacer>

      <v-btn
        text
        class="text-capitalize"
        @click.stop.prevent="cancel"
      >
        Cancel
      </v-btn>

      <v-btn
        color="primary"
        dark
        class="v-btn-with-radius"
        @click.stop.prevent="submit('close')"
      >
        Save
      </v-btn>
    </v-card-actions>
  </v-container>
</template>

<script>
import _ from "lodash";
import roleService from "../service";
import employeeService from "../../../service.js";

const { validationMixin } = require("vuelidate");
const { required, minLength } = require("vuelidate/lib/validators");

export default {
  name: "role-create",
  props: {
    isModal: Boolean,
  },

  data() {
    return {
      search: "",
      newPermissions: [],       // grouped object
      permissions: [],          // flat list from API
      historyDays: 1,
      limitedHistoryEnabled: true,

      role: {
        name: null,
      },
    };
  },

  mixins: [validationMixin],

  created() {
    employeeService.getTerminalPermissions().then((response) => {
      this.permissions = response.data;

      this.permissions.forEach((permission) => {
        // Use defaultPermission as you requested
        permission.allow = permission.defaultPermission;

        if (permission.stringId === "HISTORYLIMITEDACCESS") {
          if (!permission.metadata) {
            permission.metadata = { days: 1 };
          }
          this.historyDays = permission.metadata.days || 1;
          this.limitedHistoryEnabled = permission.defaultPermission;
        }
      });

      // Group by permission group name
      this.newPermissions = _.mapValues(
        _.groupBy(
          this.permissions,
          (permission) => permission.TPermissionGroup.name
        ),
        (list) => list
      );
    });
  },

  computed: {
    nameErrors() {
      const errors = [];
      if (!this.$v.role.name.$dirty) return errors;
      if (!this.$v.role.name.required) errors.push("Name is required.");
      if (!this.$v.role.name.minLength)
        errors.push("Must be at least 4 characters long.");
      return errors;
    },

    daysLimit() {
      return (value) => {
        this.historyDays = value;
        if (!value || value < 1) {
          return "Number of days cannot be less than 1";
        }
        return true;
      };
    },

    // Filter groups + permissions based on search text
    filteredGroups() {
      const term = (this.search || "").toLowerCase().trim();

      const groups = [];

      Object.keys(this.newPermissions).forEach((groupName) => {
        const perms = this.newPermissions[groupName].filter((p) => {
          if (!term) return true;

          const inGroup = groupName.toLowerCase().includes(term);
          const inName = p.name && p.name.toLowerCase().includes(term);
          const inDesc =
            p.description && p.description.toLowerCase().includes(term);

          return inGroup || inName || inDesc;
        });

        if (perms.length) {
          groups.push({
            name: groupName,
            permissions: perms,
          });
        }
      });

      return groups;
    },
  },

  methods: {
    switchChanged(val) {
      if (val === "HISTORYLIMITEDACCESS") {
        this.limitedHistoryEnabled = !this.limitedHistoryEnabled;
      }
    },

    submit() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.$toasted.error("Please fix invalid fields.", {
          position: "top-right",
        });
        return;
      }

      if (this.historyDays <= 0) {
        this.$toasted.error("Number of days cannot be less than 1.", {
          position: "top-right",
        });
        return;
      }

      this.$Progress.start();
      this.role.Permissions = this.permissions;

      roleService
        .create(this.role)
        .then(() => {
          this.$swal.fire({
            toast: true,
            icon: "success",
            title: "Role created.",
            position: "top-end",
            timer: 3000,
            showConfirmButton: false,
          });

          this.$Progress.finish();
          this.close();
        })
        .catch((err) => {
          console.error(err);
          this.$swal.fire({
            toast: true,
            icon: "error",
            title: err.data?.message || "Failed to create role.",
            position: "top-end",
            timer: 3000,
            showConfirmButton: false,
          });
          this.$Progress.fail();
        });
    },

    cancel() {
      this.close();
    },

    close() {
      if (!this.isModal) {
        this.$router.go(-1);
      } else {
        this.$emit("close");
      }
    },
  },

  validations: {
    role: {
      name: {
        required,
        minLength: minLength(4),
      },
    },
  },
};
</script>

<style scoped>
/* Optional styling tweaks for readability */
</style>